import client from './client';
import API_CONFIG from '../config';
import logger from '../utils/logger';

// Helper function to get specific error messages
const getErrorMessage = (error) => {
  if (error.response) {
    // Server responded with error status
    const { status, data } = error.response;
    switch (status) {
      case 400:
        return data?.message || 'Données invalides. Vérifiez vos informations.';
      case 401:
        return 'Session expirée. Veuillez vous reconnecter.';
      case 403:
        return 'Accès refusé. Vous n\'avez pas les permissions nécessaires.';
      case 404:
        return 'Ressource non trouvée.';
      case 409:
        return data?.message || 'Conflit de données.';
      case 422:
        return data?.message || 'Données invalides.';
      case 500:
        return 'Erreur serveur. Veuillez réessayer plus tard.';
      default:
        return data?.message || `Erreur ${status}`;
    }
  } else if (error.request) {
    // Network error
    return 'Erreur de connexion. Vérifiez votre connexion internet.';
  } else {
    // Other error
    return 'Une erreur inattendue s\'est produite.';
  }
};

// ============ GLOBAL ANALYTICS ============

export const fetchGlobalStats = async () => {
  try {
    const response = await client.get(API_CONFIG.ENDPOINTS.ANALYTICS.GLOBAL_STATS);
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
};

export const fetchRecentActivities = async (params = {}) => {
  try {
    const response = await client.get(API_CONFIG.ENDPOINTS.ANALYTICS.RECENT_ACTIVITIES, {
      params: {
        ...params,
        limit: params.limit || 10 // Valeur par défaut de 10 éléments
      }
    });
    return response.data || { results: [] }; // Retourne l'objet complet avec results
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
};

export const fetchSurveyMonthlyTrend = async (params = {}) => {
  try {
    const response = await client.get(API_CONFIG.ENDPOINTS.ANALYTICS.SURVEY_MONTHLY_TREND, { params });
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
};

// ============ GERANT ANALYTICS ============

// Statistiques spécifiques au gérant (portée limitée à ses sondages)
export const fetchGerantStats = async (params = {}) => {
  try {
    const response = await client.get(API_CONFIG.ENDPOINTS.ANALYTICS.GERANT_STATS, { params });
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
};

// Dernières réponses liées aux sondages du gérant
export const fetchRecentResponses = async (params = {}) => {
  try {
    const response = await client.get(API_CONFIG.ENDPOINTS.ANALYTICS.RECENT_RESPONSES, { params });
    // Backend renvoie { count, results, filters }
    return response.data?.results || [];
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
};

// KPIs fiabilisés pour le dashboard gérant
export const fetchDashboardStats = async () => {
  try {
    const response = await client.get(API_CONFIG.ENDPOINTS.ANALYTICS.DASHBOARD_STATS);
    return response.data; // { active_surveys, closed_surveys, archived_surveys }
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
};

// Récupérer les réponses par jour pour le graphique
export const fetchResponsesPerDay = async (params = {}) => {
  try {
    const response = await client.get(API_CONFIG.ENDPOINTS.ANALYTICS.RESPONSES_PER_DAY || '/api/analytics/responses-per-day/', { params });
    return response.data;
  } catch (error) {
    // Retourner des données de fallback en cas d'erreur
    logger.warn('Erreur lors de la récupération des réponses par jour:', error);
    return [];
  }
};

// Récupérer les utilisateurs récents
export const fetchRecentUsers = async (params = {}) => {
  try {
    const response = await client.get(API_CONFIG.ENDPOINTS.ANALYTICS.RECENT_USERS, {
      params: {
        ...params,
        limit: params.limit || 10
      }
    });
    return response.data?.results || [];
  } catch (error) {
    // Retourner des données de fallback en cas d'erreur
    return [
      {
        id: 1,
        nom: 'Wakhoub Gueye',
        email: 'wakhoubgueye7@gmail.com',
        role: 'Gérant',
        dateInscription: '2025-01-03'
      },
      {
        id: 2,
        nom: 'Marie Diop',
        email: 'marie.diop@example.com',
        role: 'Participant',
        dateInscription: '2025-01-02'
      },
      {
        id: 3,
        nom: 'Amadou Ba',
        email: 'amadou.ba@example.com',
        role: 'Participant',
        dateInscription: '2025-01-01'
      }
    ];
  }
};

// ============ AUDIT LOGS ============

export const fetchAuditLogs = async (params = {}) => {
  try {
    const response = await client.get(API_CONFIG.ENDPOINTS.AUDIT_LOGS, { params });
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
};

export const fetchAdminDashboard = async (params = {}) => {
  try {
    const response = await client.get(API_CONFIG.ENDPOINTS.ANALYTICS.GLOBAL_STATS, { params });
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
};

export const fetchActivityLogs = async (params = {}) => {
  try {
    const response = await client.get('/api/admin/activity-logs/', { params });
    return response.data; // { data, total, page, page_size, pages }
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
};


// ============ FEEDBACK ============

// Function to submit feedback
export const submitFeedback = async (feedbackData) => {
  throw new Error('Feedback functionality not implemented');
};

// Function to get feedbacks for admin
export const getFeedbacks = async () => {
  throw new Error('Feedback functionality not implemented');
};

// Function to update feedback status
export const updateFeedbackStatus = async (id, status) => {
  throw new Error('Feedback functionality not implemented');
};


// ============ ADMIN DASHBOARD ============

